// Step 24 — deterministic readiness summary.
//
// Renders authoritative COUNTS from the underlying endpoints. There
// is NO overall readiness score, NO closing probability, NO health
// score. Every tile either shows a real count OR the neutral
// "Ingen auktoritativ status registrerad" text.
//
// Failed loads render as "—" (not zero) with the degraded state
// coming from AIExecutionSummary's `errors` prop passthrough.

import { AISection } from "../AIPrimitives";
import {
  READINESS_LANGUAGE,
  executionActionCounts,
  executionContractCounts,
  executionObligationRows,
  executionRiskRows,
  executionBuyerRows,
} from "./helpers";

function _tile(label, valueOrDash, hint) {
  return (
    <div className="qai-execution-readiness-tile">
      <div className="qai-execution-readiness-tile-value">{valueOrDash}</div>
      <div className="qai-execution-readiness-tile-label">{label}</div>
      {hint && (
        <div className="qai-execution-readiness-tile-hint">{hint}</div>
      )}
    </div>
  );
}

export default function AIExecutionReadiness({
  actions,
  contracts,
  situation,
  errors = {},
  now,
}) {
  const aCounts = errors.actions
    ? null
    : executionActionCounts(actions, { now });
  const cCounts = errors.contracts
    ? null
    : executionContractCounts(contracts);
  const preparedness = errors.situation
    ? null
    : executionObligationRows(situation);
  const risks = errors.situation
    ? null
    : executionRiskRows(situation?.deal);
  const buyers = errors.situation
    ? null
    : executionBuyerRows(situation?.buyer_portfolio);
  const silent = buyers ? buyers.filter((b) => b.is_silent).length : null;

  return (
    <AISection
      title="Genomförandeöversikt"
      right={
        <span className="qai-page-section-hint">
          Deterministiska räknare · inga påhittade poäng
        </span>
      }
    >
      <div className="qai-execution-readiness-grid">
        {_tile("Öppna åtgärder",
          aCounts ? aCounts.open : "—",
          errors.actions ? "Källa ej tillgänglig" : null)}
        {_tile("Blockerade åtgärder",
          aCounts ? aCounts.blocked : "—",
          errors.actions ? "Källa ej tillgänglig" : null)}
        {_tile("Försenade åtgärder",
          aCounts ? aCounts.overdue : "—",
          errors.actions ? "Källa ej tillgänglig" : null)}
        {_tile("Åtgärder väntar granskning",
          aCounts ? aCounts.pending_review : "—",
          errors.actions ? "Källa ej tillgänglig" : null)}
        {_tile("Klara åtgärder",
          aCounts ? aCounts.completed : "—",
          errors.actions ? "Källa ej tillgänglig" : null)}
        {_tile("Avtal totalt",
          cCounts ? cCounts.total : "—",
          errors.contracts ? "Källa ej tillgänglig" : null)}
        {_tile("Avtal väntar granskning",
          cCounts ? cCounts.pending_review : "—",
          errors.contracts ? "Källa ej tillgänglig" : null)}
        {_tile("Avtal med osäkerheter",
          cCounts ? cCounts.with_uncertainties : "—",
          errors.contracts ? "Källa ej tillgänglig" : null)}
        {_tile("Beredskapsgap",
          preparedness ? preparedness.length : "—",
          errors.situation ? "Källa ej tillgänglig" : null)}
        {_tile("Registrerade risker",
          risks ? risks.length : "—",
          errors.situation ? "Källa ej tillgänglig" : null)}
        {_tile("Tysta köpare",
          silent != null ? silent : "—",
          errors.situation ? "Källa ej tillgänglig" : null)}
      </div>
      <div className="qai-execution-readiness-boundary">
        Vyn visar deterministisk data från deal_situation, deal_actions
        och contract_drafts. Inga sammansatta sannolikhets- eller
        hälso-poäng beräknas — genomförandebeslut ligger hos användaren
        via granskningsvyerna.
        {(errors.actions || errors.contracts || errors.situation) && (
          <>
            {" "}
            {READINESS_LANGUAGE.NO_STATUS}: en eller flera källor kunde
            inte laddas — saknade räknare visas som "—" (inte 0).
          </>
        )}
      </div>
    </AISection>
  );
}
